import React, { useState, useEffect } from 'react';
import { usersService } from '../services/users.service';
import { colocService, Coloc } from '../services/coloc.service';
import { UserResponse, CreateUserRequest, UpdateUserRequest } from '../types/user';
import '../styles/UsersPage.css';
import { getErrorMessage } from '../utils/error';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [colocs, setColocs] = useState<Coloc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserResponse | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState('TENANT');
  const [formColocIds, setFormColocIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, colocsData] = await Promise.all([
        usersService.getUsers(),
        colocService.getColocs(),
      ]);
      setUsers(usersData.filter((u: UserResponse) => u.role === 'TENANT'));
      setColocs(colocsData);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement des utilisateurs'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormFirstName('');
    setFormLastName('');
    setFormEmail('');
    setFormRole('TENANT');
    setFormColocIds([]);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (user: UserResponse) => {
    setEditingUser(user);
    setFormFirstName(user.firstName);
    setFormLastName(user.lastName);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormColocIds(user.colocs.map(c => c.id));
    setFormError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormLoading(true);

    try {
      if (editingUser) {
        const data: UpdateUserRequest = {
          first_name: formFirstName,
          last_name: formLastName,
          email: formEmail,
          role: formRole,
          coloc_ids: formColocIds,
        };
        await usersService.updateUser(editingUser.id, data);
      } else {
        const data: CreateUserRequest = {
          first_name: formFirstName,
          last_name: formLastName,
          email: formEmail,
          role: formRole,
          coloc_ids: formColocIds,
        };
        await usersService.createUser(data);
      }

      setShowModal(false);
      await loadData();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await usersService.deleteUser(userId);
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la suppression'));
    }
  };

  const handleColocToggle = (colocId: string) => {
    setFormColocIds(prev =>
      prev.includes(colocId)
        ? prev.filter(id => id !== colocId)
        : [...prev, colocId]
    );
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return <div className="users-loading">Chargement...</div>;
  }

  return (
    <div className="users-page">
      <div className="users-header">
        <h2>Locataires</h2>
        <button className="users-add-btn" onClick={openCreateModal}>
          Ajouter un locataire
        </button>
      </div>

      {error && <div className="users-error">{error}</div>}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Colocations</th>
              <th>Date création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>
                  {user.colocs.length > 0
                    ? user.colocs.map(c => c.name).join(', ')
                    : <span className="no-coloc">Aucune</span>
                  }
                </td>
                <td>{formatDate(user.dateCreated)}</td>
                <td className="users-actions">
                  <button className="users-edit-btn" onClick={() => openEditModal(user)}>
                    Modifier
                  </button>
                  {deleteConfirm === user.id ? (
                    <span className="delete-confirm">
                      <button className="users-confirm-delete-btn" onClick={() => handleDelete(user.id)}>
                        Confirmer
                      </button>
                      <button className="users-cancel-btn" onClick={() => setDeleteConfirm(null)}>
                        Annuler
                      </button>
                    </span>
                  ) : (
                    <button className="users-delete-btn" onClick={() => setDeleteConfirm(user.id)}>
                      Supprimer
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr className="users-empty-row">
                <td colSpan={5} className="users-empty">Aucun locataire</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editingUser ? 'Modifier le locataire' : 'Nouveau locataire'}</h3>

            {formError && <div className="users-error">{formError}</div>}

            <form onSubmit={handleSubmit} className="users-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={formFirstName}
                    onChange={e => setFormFirstName(e.target.value)}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={formLastName}
                    onChange={e => setFormLastName(e.target.value)}
                    required
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  required
                  className="form-control"
                />
              </div>

              {/* Plus de champ secret : ni à la création, ni à la modification.
                  Un bailleur ne détient pas l'accès d'un tiers, et ne le change
                  pas non plus (audit du 2026-08-17, P1-4). Le locataire passe
                  par son lien d'invitation, ou par « mot de passe oublié ». */}
              {!editingUser && (
                <p className="access-hint">
                  Le locataire recevra un email avec un lien pour choisir son mot
                  de passe. Vous ne le connaîtrez jamais.
                </p>
              )}

              <div className="form-group">
                <label>Colocations</label>
                <div className="coloc-checkboxes">
                  {colocs.map(coloc => (
                    <label key={coloc.id} className="coloc-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formColocIds.includes(coloc.id)}
                        onChange={() => handleColocToggle(coloc.id)}
                      />
                      {coloc.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="users-cancel-btn" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="users-save-btn" disabled={formLoading}>
                  {formLoading ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
